/**
 * Cloudinary Upload Module
 * Client-side image upload with validation and progress tracking
 */

(function(window) {
    'use strict';

    // Cloudinary Configuration
    const CLOUDINARY_CONFIG = {
        cloudName: 'dlweltnyv', // Cloudinary cloud name
        uploadPreset: 'bluemint_custom_products', // Upload preset
        folder: 'bluemint-custom-products',
        maxFileSize: 5 * 1024 * 1024, // 5MB
        allowedFormats: ['jpg', 'jpeg', 'png'],
        apiUrl: 'https://api.cloudinary.com/v1_1/'
    };

    /**
     * Upload image to Cloudinary
     * @param {File|Blob|Canvas} source - Image source (File, Blob, or Canvas)
     * @param {string} type - Upload type ('original' or 'rendered')
     * @param {Function} onProgress - Progress callback (optional)
     * @returns {Promise<string>} - Cloudinary URL
     */
    async function uploadToCloudinary(source, type = 'original', onProgress = null) {
        try {
            // Convert source to blob if it's a canvas
            let blob;
            if (source instanceof HTMLCanvasElement) {
                blob = await canvasToBlob(source);
            } else if (source instanceof File || source instanceof Blob) {
                blob = source;
            } else {
                throw new Error('Geçersiz görsel formatı');
            }

            // Create form data
            const formData = new FormData();
            formData.append('file', blob);
            formData.append('upload_preset', CLOUDINARY_CONFIG.uploadPreset);
            formData.append('folder', `${CLOUDINARY_CONFIG.folder}/${type}`);
            formData.append('timestamp', Date.now());

            // Add tags
            const tags = ['custom-product', type];
            formData.append('tags', tags.join(','));

            // Upload with XMLHttpRequest for progress tracking
            const url = `${CLOUDINARY_CONFIG.apiUrl}${CLOUDINARY_CONFIG.cloudName}/image/upload`;
            
            return new Promise((resolve, reject) => {
                const xhr = new XMLHttpRequest();

                // Progress tracking
                if (onProgress) {
                    xhr.upload.addEventListener('progress', (e) => {
                        if (e.lengthComputable) {
                            const percentComplete = (e.loaded / e.total) * 100;
                            onProgress(percentComplete);
                        }
                    });
                }

                // Success handler
                xhr.addEventListener('load', () => {
                    if (xhr.status === 200) {
                        try {
                            const response = JSON.parse(xhr.responseText);
                            resolve(response.secure_url);
                        } catch (error) {
                            reject(new Error('Cloudinary yanıt formatı hatalı'));
                        }
                    } else {
                        reject(new Error(`Upload hatası: ${xhr.status}`));
                    }
                });

                // Error handler
                xhr.addEventListener('error', () => {
                    reject(new Error('Görsel yüklenemedi. İnternet bağlantınızı kontrol edin.'));
                });

                // Timeout handler (30 seconds)
                xhr.timeout = 30000;
                xhr.addEventListener('timeout', () => {
                    reject(new Error('Yükleme zaman aşımına uğradı. Lütfen tekrar deneyin.'));
                });

                xhr.open('POST', url);
                xhr.send(formData);
            });

        } catch (error) {
            console.error('Cloudinary upload error:', error);
            throw error;
        }
    }

    /**
     * Upload with retry logic
     * @param {File|Blob|Canvas} source - Image source
     * @param {string} type - Upload type
     * @param {Function} onProgress - Progress callback
     * @param {number} maxRetries - Maximum retry attempts
     * @returns {Promise<string>} - Cloudinary URL
     */
    async function uploadWithRetry(source, type = 'original', onProgress = null, maxRetries = 3) {
        let lastError;
        
        for (let attempt = 1; attempt <= maxRetries; attempt++) {
            try {
                const url = await uploadToCloudinary(source, type, onProgress);
                return url;
            } catch (error) {
                lastError = error;
                console.warn(`Upload attempt ${attempt} failed:`, error.message);
                
                if (attempt < maxRetries) {
                    // Wait before retry (exponential backoff)
                    const delay = Math.min(1000 * Math.pow(2, attempt - 1), 5000);
                    await sleep(delay);
                }
            }
        }
        
        throw lastError;
    }

    /**
     * Convert canvas to blob
     * @param {HTMLCanvasElement} canvas
     * @returns {Promise<Blob>}
     */
    function canvasToBlob(canvas) {
        return new Promise((resolve, reject) => {
            canvas.toBlob((blob) => {
                if (blob) {
                    resolve(blob);
                } else {
                    reject(new Error('Canvas blob dönüşümü başarısız'));
                }
            }, 'image/png', 0.95);
        });
    }

    /**
     * Sleep utility
     * @param {number} ms - Milliseconds
     * @returns {Promise}
     */
    function sleep(ms) {
        return new Promise(resolve => setTimeout(resolve, ms));
    }

    /**
     * Validate file before upload
     * @param {File} file
     * @returns {Object} - {isValid: boolean, error: string}
     */
    function validateFile(file) {
        // Check if file exists
        if (!file) {
            return { isValid: false, error: 'Dosya seçilmedi' };
        }

        // Check file size
        if (file.size > CLOUDINARY_CONFIG.maxFileSize) {
            const maxSizeMB = CLOUDINARY_CONFIG.maxFileSize / (1024 * 1024);
            return { 
                isValid: false, 
                error: `Dosya boyutu ${maxSizeMB}MB'dan küçük olmalıdır` 
            };
        }

        // Check file format
        const extension = file.name.split('.').pop().toLowerCase();
        const mimeType = file.type.toLowerCase();
        
        const isValidExtension = CLOUDINARY_CONFIG.allowedFormats.includes(extension);
        const isValidMimeType = CLOUDINARY_CONFIG.allowedFormats.some(format => 
            mimeType.includes(format)
        );

        if (!isValidExtension && !isValidMimeType) {
            return { 
                isValid: false, 
                error: 'Sadece JPG ve PNG formatları desteklenir' 
            };
        }

        return { isValid: true };
    }

    /**
     * Get file info
     * @param {File} file
     * @returns {Object}
     */
    function getFileInfo(file) {
        return {
            name: file.name,
            size: file.size,
            sizeFormatted: formatFileSize(file.size),
            type: file.type,
            lastModified: new Date(file.lastModified)
        };
    }

    /**
     * Format file size
     * @param {number} bytes
     * @returns {string}
     */
    function formatFileSize(bytes) {
        if (bytes === 0) return '0 Bytes';
        const k = 1024;
        const sizes = ['Bytes', 'KB', 'MB', 'GB'];
        const i = Math.floor(Math.log(bytes) / Math.log(k));
        return Math.round(bytes / Math.pow(k, i) * 100) / 100 + ' ' + sizes[i];
    }

    // Export to global scope
    window.CloudinaryUpload = {
        upload: uploadToCloudinary,
        uploadWithRetry: uploadWithRetry,
        validateFile: validateFile,
        getFileInfo: getFileInfo,
        config: CLOUDINARY_CONFIG
    };

})(window);
